'use client';

import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router';
import {
  Sparkles,
  Users,
  Target,
  Share2,
  Film,
  Map,
  Copy,
  Trash2,
  Clock,
  AlertCircle,
  Loader2,
  ArrowRight,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { AskAIBar } from '@/components/ai';
import type { Citation } from '@/lib/ai-client';
import { VersionsDropdown, type Version } from '@/components/versions';
import { ArtifactGeneratorInput, type AttachedFile } from '@/components/shared/artifact-generator-input';
import { AIGenerationProgress, type ArtifactType } from '@/components/discovery/ai-generation-progress';
import { DefinitionDetailView } from './definition-detail-view';
import { useToast } from '@/hooks/use-toast';
import { useAuthToken } from '@/hooks/use-auth';
import { 
  useDefinition, 
  useCreateDefinitionRow, 
  useUpdateDefinitionRow, 
  useDeleteDefinitionRow,
  useCopyDefinitionRow,
  useGenerateDefinition,
  useDefinitionVersions,
  useRestoreDefinitionVersion,
  type DefinitionType,
  type DefinitionPersona,
  type DefinitionUseCase,
  type DefinitionJourney,
  type DefinitionServiceMap,
  type DefinitionStoryboard,
  type DefinitionItem,
} from '@/hooks/use-definition';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';

type DefinitionView = 'personas' | 'use-cases' | 'journeys' | 'service-maps' | 'storyboards';

interface DefinitionConfig {
  id: DefinitionView;
  title: string;
  subtitle: string;
  icon: React.ElementType;
  emptyTitle: string;
  emptyDescription: string;
  placeholder: string;
  askAIPlaceholder: string;
  hasInlineForm: boolean;
}

const definitionConfigs: Record<DefinitionView, DefinitionConfig> = {
  personas: {
    id: 'personas',
    title: 'Personas',
    subtitle: 'User personas for',
    icon: Users,
    emptyTitle: 'Create User Personas',
    emptyDescription: 'Generate detailed user personas by describing your target users or uploading research documents.',
    placeholder: "Describe your target users...",
    askAIPlaceholder: "E.g., Add more detailed demographics, focus on B2B users...",
    hasInlineForm: true,
  },
  'use-cases': {
    id: 'use-cases',
    title: 'Use Cases',
    subtitle: 'Use cases for',
    icon: Target,
    emptyTitle: 'Define Use Cases',
    emptyDescription: 'Generate comprehensive use cases by describing system functionality.',
    placeholder: "Describe the system functionality...",
    askAIPlaceholder: "E.g., Add edge cases, focus on error handling...",
    hasInlineForm: true,
  },
  journeys: {
    id: 'journeys',
    title: 'User Journeys',
    subtitle: 'User journeys for',
    icon: Share2,
    emptyTitle: 'Map User Journeys',
    emptyDescription: 'Generate user journey maps by describing user scenarios.',
    placeholder: "Describe user scenarios...",
    askAIPlaceholder: "E.g., Add emotional touchpoints, focus on pain points...",
    hasInlineForm: false,
  },
  'service-maps': {
    id: 'service-maps',
    title: 'Service Maps',
    subtitle: 'Service maps for',
    icon: Map,
    emptyTitle: 'Design Service Maps',
    emptyDescription: 'Generate comprehensive service blueprints by describing service architecture.',
    placeholder: "Describe your service architecture...",
    askAIPlaceholder: "E.g., Add caching layer, include failure points...",
    hasInlineForm: false,
  },
  storyboards: {
    id: 'storyboards',
    title: 'Storyboards',
    subtitle: 'Storyboards for',
    icon: Film,
    emptyTitle: 'Create Storyboards',
    emptyDescription: 'Generate visual storyboards by describing user scenarios.',
    placeholder: "Describe scenarios to visualize...",
    askAIPlaceholder: "E.g., Add more frames, focus on error states...",
    hasInlineForm: false,
  },
};

const statusColors: Record<string, string> = {
  DRAFT: 'bg-gray-100 text-gray-700',
  IN_REVIEW: 'bg-blue-100 text-blue-700',
  APPROVED: 'bg-green-100 text-green-700',
  ACTIVE: 'bg-green-100 text-green-700',
  ARCHIVED: 'bg-gray-100 text-gray-500',
};

const priorityColors: Record<string, string> = {
  CRITICAL: 'bg-red-100 text-red-700',
  HIGH: 'bg-orange-100 text-orange-700',
  MEDIUM: 'bg-yellow-100 text-yellow-700',
  LOW: 'bg-gray-100 text-gray-600',
};

// Section definitions for Ask AI section-level editing
interface SectionOption {
  path: string;
  label: string;
  description: string;
}

const SECTION_OPTIONS: Record<DefinitionView, SectionOption[]> = {
  personas: [
    { path: 'name', label: 'Name', description: 'Persona name and identity' },
    { path: 'role', label: 'Role', description: 'Professional role and responsibilities' },
    { path: 'demographics', label: 'Demographics', description: 'Age, location, tech proficiency' },
    { path: 'primaryGoals', label: 'Primary Goals', description: 'Main objectives and motivations' },
    { path: 'challenges', label: 'Challenges', description: 'Pain points and obstacles' },
    { path: 'contexts', label: 'Usage Contexts', description: 'When and where they use the product' },
    { path: 'tools', label: 'Tools & Technologies', description: 'Tools they currently use' },
    { path: 'motivations', label: 'Motivations', description: 'What drives their behavior' },
  ],
  'use-cases': [
    { path: 'title', label: 'Title', description: 'Use case name' },
    { path: 'description', label: 'Description', description: 'Detailed use case description' },
    { path: 'actor', label: 'Actor', description: 'Primary actor performing the use case' },
    { path: 'preconditions', label: 'Preconditions', description: 'Requirements before execution' },
    { path: 'postconditions', label: 'Postconditions', description: 'State after completion' },
    { path: 'mainFlow', label: 'Main Flow', description: 'Primary steps sequence' },
    { path: 'alternativeFlows', label: 'Alternative Flows', description: 'Variations and edge cases' },
    { path: 'exceptions', label: 'Exceptions', description: 'Error handling scenarios' },
  ],
  journeys: [
    { path: 'scenario', label: 'Scenario', description: 'Journey scenario name' },
    { path: 'description', label: 'Description', description: 'Journey description' },
    { path: 'stages', label: 'Stages', description: 'Journey stages and steps' },
    { path: 'pains', label: 'Pain Points', description: 'User frustrations and obstacles' },
    { path: 'opportunities', label: 'Opportunities', description: 'Improvement opportunities' },
  ],
  'service-maps': [
    { path: 'title', label: 'Title', description: 'Service map name' },
    { path: 'description', label: 'Description', description: 'Service overview' },
    { path: 'serviceStages', label: 'Service Stages', description: 'Stages of service delivery' },
    { path: 'systems', label: 'Systems', description: 'Backend systems involved' },
    { path: 'failurePoints', label: 'Failure Points', description: 'Potential failure scenarios' },
  ],
  storyboards: [
    { path: 'scenario', label: 'Scenario', description: 'Storyboard scenario' },
    { path: 'description', label: 'Description', description: 'Storyboard description' },
    { path: 'narrative', label: 'Narrative', description: 'Story narrative flow' },
    { path: 'frames', label: 'Frames', description: 'Visual frame sequence' },
    { path: 'assumptions', label: 'Assumptions', description: 'Key assumptions made' },
  ],
};

// Helper to get value by path from object
function getValueByPath(obj: Record<string, unknown>, path: string): unknown {
  const keys = path.split('.');
  let current: unknown = obj;
  for (const key of keys) {
    if (current == null || typeof current !== 'object') return undefined;
    current = (current as Record<string, unknown>)[key];
  }
  return current;
}

interface DefinitionPanelProps {
  projectId: string;
  projectName?: string;
  activeView?: DefinitionView;
  onViewChange?: (view: DefinitionView) => void;
}

export function DefinitionPanel({
  projectId,
  projectName = 'Project',
  activeView = 'personas',
}: DefinitionPanelProps) {
  const [showAskAI, setShowAskAI] = useState(false);
  const [editingRow, setEditingRow] = useState<any | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [generationError, setGenerationError] = useState<string | null>(null);
  const [selectedItem, setSelectedItem] = useState<DefinitionItem | null>(null);
  
  // Delete confirmation dialog state
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Track which version is currently active
  const [currentVersionId, setCurrentVersionId] = useState<string | null>(null);

  // Ask AI section-level editing state
  const [askAIItemId, setAskAIItemId] = useState<string | null>(null);
  const [askAISection, setAskAISection] = useState<string | null>(null);
  const [showSectionSelector, setShowSectionSelector] = useState(false);
  const [askAIPatchSuggestion, setAskAIPatchSuggestion] = useState<{
    patches: Array<{ op: string; path: string; value: unknown }>;
    beforeAfter: { before: string; after: string };
    rationale: string[];
    evidence: Array<{ type: string; id: string; name: string; excerpt?: string }>;
  } | null>(null);
  const [isAskAILoading, setIsAskAILoading] = useState(false);

  const { toast } = useToast();
  const token = useAuthToken();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Open detail view and update URL
  const handleOpenDetail = (item: DefinitionItem) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      newParams.set('item', item.id);
      return newParams;
    }, { replace: true });
    setSelectedItem(item);
  };
  
  // Close detail view and clear URL param
  const handleCloseDetail = () => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      newParams.delete('item');
      return newParams;
    }, { replace: true });
    setSelectedItem(null);
  };

  // Reset UI state when switching tabs
  useEffect(() => {
    setShowAskAI(false);
    setIsDialogOpen(false);
    setEditingRow(null);
    setShowVersionHistory(false);
    setFormData({});
    setGenerationError(null);
    setSelectedItem(null);
    setCurrentVersionId(null); // Reset version tracking
    // Reset Ask AI section selection state
    setAskAIItemId(null);
    setAskAISection(null);
    setShowSectionSelector(false);
    setAskAIPatchSuggestion(null);
    setIsAskAILoading(false);
  }, [activeView]);

  const config = definitionConfigs[activeView];
  const Icon = config.icon;

  // Map view to API type
  const apiType = activeView as DefinitionType;

  // Hooks
  const { data: artefact, isLoading, error, isError } = useDefinition(projectId, apiType);
  const { data: versions } = useDefinitionVersions(projectId, apiType);
  const createMutation = useCreateDefinitionRow(projectId, apiType);
  const updateMutation = useUpdateDefinitionRow(projectId, apiType);
  const deleteMutation = useDeleteDefinitionRow(projectId, apiType);
  const copyMutation = useCopyDefinitionRow(projectId, apiType);
  const generateMutation = useGenerateDefinition(projectId, apiType);
  const restoreMutation = useRestoreDefinitionVersion(projectId, apiType);

  const items = artefact?.items || [];
  const hasContent = items.length > 0;

  // Deep link handling: Open item detail when ?item=XXX is in URL
  useEffect(() => {
    const itemId = searchParams.get('item');
    if (itemId && items.length > 0) {
      // Find the item in the current data
      const foundItem = items.find((item: DefinitionItem) => item.id === itemId);
      if (foundItem) {
        setSelectedItem(foundItem);
      }
    } else if (!itemId && selectedItem) {
      // URL param was removed (e.g., by browser back), close detail view
      setSelectedItem(null);
    }
  }, [searchParams, items]);

  // Check authentication
  if (!token) {
    return (
      <div className="flex-1 flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Authentication Required</h3>
          <p className="text-gray-600">Please log in to view definition artefacts.</p>
        </div>
      </div>
    );
  }

  // Handle error state
  if (isError) {
    return (
      <div className="flex-1 flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Data</h3>
          <p className="text-gray-600 mb-4">{error?.message || 'Failed to load data'}</p>
          <Button onClick={() => window.location.reload()}>Retry</Button>
        </div>
      </div>
    );
  }

  const handleGenerate = async (description: string, attachedFiles: AttachedFile[]) => {
    setGenerationError(null);
    try {
      await generateMutation.mutateAsync({
        prompt: description,
        attachedFiles: attachedFiles.map(f => ({ name: f.name, type: f.type, size: f.size })),
        model: 'deepseek-chat',
      });
      toast({
        title: 'Content Generated',
        description: 'AI has generated your content successfully.',
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to generate content';
      setGenerationError(errorMessage);
      toast({
        title: 'Generation Failed',
        description: errorMessage,
        variant: 'destructive',
      });
    }
  };

  // Handle Ask AI - either full regeneration or section-level patch
  const handleAskAI = useCallback(async (message: string): Promise<
    | { response: string; citations: Citation[] }
    | {
        patches: Array<{ op: string; path: string; value: unknown }>;
        beforeAfter: { before: string; after: string };
        rationale: string[];
        evidence: Array<{ type: string; id: string; name: string; excerpt?: string }>;
        sectionPath?: string;
      }
    | void
  > => {
    // If no item/section selected, do full regeneration
    if (!askAIItemId || !askAISection) {
      try {
        await generateMutation.mutateAsync({
          prompt: message,
          model: 'deepseek-chat',
        });
        toast({
          title: 'AI Request Completed',
          description: 'New content has been generated.',
        });
        return;
      } catch (error) {
        toast({
          title: 'AI Request Failed',
          description: error instanceof Error ? error.message : 'Failed to process request',
          variant: 'destructive',
        });
        throw error;
      }
    }

    // Section-level Ask AI - call the ask-ai endpoint
    setIsAskAILoading(true);
    try {
      const selectedItemData = items.find((item: DefinitionItem) => item.id === askAIItemId);
      if (!selectedItemData) {
        throw new Error('Selected item not found');
      }

      // Get current section content
      const currentContent = getValueByPath(selectedItemData as Record<string, unknown>, askAISection);

      // Map view to API type for the request
      const artifactTypeMap: Record<DefinitionView, string> = {
        personas: 'PERSONA',
        'use-cases': 'USE_CASE',
        journeys: 'JOURNEY',
        'service-maps': 'SERVICE_MAP',
        storyboards: 'STORYBOARD',
      };

      const response = await fetch(
        `/api/projects/${projectId}/definition/${apiType}/rows/${askAIItemId}/ask-ai`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            sectionPath: askAISection,
            instruction: message,
            currentContent,
            fullArtifact: selectedItemData,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Request failed: ${response.status}`);
      }

      const result = await response.json();

      // Return the patch suggestion for the AskAIBar to display
      const patchSuggestion = {
        patches: result.patches || [],
        beforeAfter: result.beforeAfter || { before: String(currentContent || ''), after: '' },
        rationale: result.rationale || [],
        evidence: result.evidence || [],
        sectionPath: askAISection,
      };

      setAskAIPatchSuggestion(patchSuggestion);
      return patchSuggestion;
    } catch (error) {
      toast({
        title: 'Ask AI Failed',
        description: error instanceof Error ? error.message : 'Failed to get AI suggestion',
        variant: 'destructive',
      });
      throw error;
    } finally {
      setIsAskAILoading(false);
    }
  }, [askAIItemId, askAISection, generateMutation, items, projectId, apiType, token, toast]);

  // Handle applying a patch suggestion
  const handleApplyPatch = useCallback(async (
    suggestion: {
      patches: Array<{ op: string; path: string; value: unknown }>;
      beforeAfter: { before: string; after: string };
      rationale: string[];
      evidence: Array<{ type: string; id: string; name: string; excerpt?: string }>;
      sectionPath?: string;
    },
    editedContent?: string
  ) => {
    if (!askAIItemId) return;

    try {
      const response = await fetch(
        `/api/projects/${projectId}/definition/${apiType}/rows/${askAIItemId}/ask-ai/apply`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            patches: suggestion.patches,
            editedContent,
            sectionPath: suggestion.sectionPath || askAISection,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to apply patch: ${response.status}`);
      }

      // Reset state and refresh data
      setAskAIPatchSuggestion(null);
      setAskAIItemId(null);
      setAskAISection(null);
      setShowAskAI(false);

      toast({
        title: 'Changes Applied',
        description: 'The AI suggestion has been applied successfully.',
      });
    } catch (error) {
      toast({
        title: 'Apply Failed',
        description: error instanceof Error ? error.message : 'Failed to apply changes',
        variant: 'destructive',
      });
      throw error;
    }
  }, [askAIItemId, askAISection, projectId, apiType, token, toast]);

  // Open Ask AI for a specific item and section
  const handleOpenAskAIForSection = useCallback((itemId: string, sectionPath: string) => {
    setAskAIItemId(itemId);
    setAskAISection(sectionPath);
    setShowSectionSelector(false);
    setShowAskAI(true);
    setAskAIPatchSuggestion(null);
  }, []);

  // Toggle section selector for an item
  const handleToggleSectionSelector = useCallback((itemId: string) => {
    if (showSectionSelector && askAIItemId === itemId) {
      setShowSectionSelector(false);
      setAskAIItemId(null);
    } else {
      setAskAIItemId(itemId);
      setShowSectionSelector(true);
      setAskAISection(null);
    }
  }, [showSectionSelector, askAIItemId]);

  const handleOpenDialog = (row?: any) => {
    if (row) {
      setEditingRow(row);
      setFormData({ ...row });
    } else {
      setEditingRow(null);
      setFormData({});
    }
    setIsDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      if (editingRow) {
        await updateMutation.mutateAsync({
          rowId: editingRow.id,
          data: formData,
        });
        toast({ title: 'Updated', description: 'Item updated successfully.' });
      } else {
        await createMutation.mutateAsync(formData);
        toast({ title: 'Created', description: 'Item created successfully.' });
      }
      setIsDialogOpen(false);
      setFormData({});
      setEditingRow(null);
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to save',
        variant: 'destructive',
      });
    }
  };

  const handleDeleteClick = (id: string) => {
    setItemToDelete(id);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;
    setIsDeleting(true);
    try {
      await deleteMutation.mutateAsync(itemToDelete);
      toast({ title: 'Deleted', description: 'Item deleted successfully.' });
      setDeleteConfirmOpen(false);
      setItemToDelete(null);
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to delete',
        variant: 'destructive',
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false);
    setItemToDelete(null);
  };

  const handleCopy = async (id: string) => {
    try {
      await copyMutation.mutateAsync(id);
      toast({ title: 'Copied', description: 'Item duplicated successfully.' });
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to copy',
        variant: 'destructive',
      });
    }
  };

  const handleRestoreVersion = async (versionId: string) => {
    try {
      await restoreMutation.mutateAsync(versionId);
      // Update which version is now current
      setCurrentVersionId(versionId);
      toast({ title: 'Version Switched', description: 'Switched to selected version.' });
      setShowVersionHistory(false);
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to switch version',
        variant: 'destructive',
      });
    }
  };

  const handleChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Render personas table (matches Figma design)
  const renderPersonasTable = () => {
    const personas = items as DefinitionPersona[];
    return (
      <div className="grid grid-cols-1 gap-4">
        {personas.map((persona) => (
          <div
            key={persona.id}
            className="bg-white rounded-xl border border-gray-200 hover:border-brand-primary/30 hover:shadow-sm transition-all cursor-pointer"
            onClick={() => handleOpenDetail(persona)}
          >
            <div className="p-6">
              {/* Header: Name + Role + Priority + Actions */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-brand-primary to-purple-500 flex items-center justify-center text-white font-semibold text-lg shrink-0">
                    {(persona.name || '?').charAt(0)}
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{persona.name}</h3>
                    {persona.role && (
                      <p className="text-sm text-brand-primary">{persona.role}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                  {persona.priority && (
                    <span className={cn(
                      "px-2.5 py-1 text-xs font-medium rounded-full mr-2",
                      persona.priority === 'CRITICAL' && "bg-red-100 text-red-700",
                      persona.priority === 'HIGH' && "bg-orange-100 text-orange-700",
                      persona.priority === 'MEDIUM' && "bg-yellow-100 text-yellow-700",
                      persona.priority === 'LOW' && "bg-gray-100 text-gray-600",
                    )}>
                      {persona.priority.charAt(0) + persona.priority.slice(1).toLowerCase()}
                    </span>
                  )}
                  <button
                    onClick={() => handleToggleSectionSelector(persona.id)}
                    className={cn(
                      "p-2 rounded-lg transition-colors",
                      showSectionSelector && askAIItemId === persona.id
                        ? "bg-indigo-100 text-brand-primary"
                        : "hover:bg-indigo-50 text-brand-primary/60 hover:text-brand-primary"
                    )}
                    title="Ask AI to improve"
                  >
                    <Sparkles className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleOpenDetail(persona)}
                    className="p-2 hover:bg-gray-100 rounded-lg text-brand-primary hover:text-brand-primary/80"
                    title="View details"
                  >
                    <ArrowRight className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDeleteClick(persona.id)}
                    className="p-2 hover:bg-red-50 rounded-lg text-red-400 hover:text-red-600"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Demographics summary */}
              {persona.demographics && (
                <p className="text-sm text-gray-600 mb-4 line-clamp-2">{persona.demographics}</p>
              )}

              {/* Goals + Challenges side by side */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {persona.primaryGoals && (
                  <div className="bg-green-50 rounded-lg p-3">
                    <h4 className="text-xs font-semibold text-green-700 uppercase tracking-wider mb-1.5">Goals</h4>
                    <p className="text-sm text-gray-700 line-clamp-3">{persona.primaryGoals}</p>
                  </div>
                )}
                {persona.challenges && (
                  <div className="bg-red-50 rounded-lg p-3">
                    <h4 className="text-xs font-semibold text-red-700 uppercase tracking-wider mb-1.5">Challenges</h4>
                    <p className="text-sm text-gray-700 line-clamp-3">{persona.challenges}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Priority colors for colored badges (like Figma)
  const priorityBadgeColors: Record<string, string> = {
    CRITICAL: 'bg-red-100 text-red-700 border border-red-200',
    HIGH: 'bg-red-50 text-red-600 border border-red-200',
    MEDIUM: 'bg-yellow-50 text-yellow-700 border border-yellow-200',
    LOW: 'bg-gray-100 text-gray-600 border border-gray-200',
  };

  // Status colors for colored badges (like Figma)
  const statusBadgeColors: Record<string, string> = {
    DRAFT: 'bg-gray-100 text-gray-600 border border-gray-200',
    IN_REVIEW: 'bg-yellow-50 text-yellow-700 border border-yellow-200',
    APPROVED: 'bg-green-50 text-green-600 border border-green-200',
    ACTIVE: 'bg-green-50 text-green-600 border border-green-200',
    ARCHIVED: 'bg-gray-100 text-gray-500 border border-gray-200',
  };

  // Format status text for display
  const formatStatus = (status: string) => {
    return status.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    ).join(' ');
  };

  // Render use cases table
  const renderUseCasesTable = () => {
    const useCases = items as DefinitionUseCase[];
    return (
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="text-left text-sm font-medium text-gray-600 px-6 py-4">Use Case</th>
              <th className="text-left text-sm font-medium text-gray-600 px-6 py-4">Description</th>
              <th className="text-center text-sm font-medium text-gray-600 px-6 py-4">Steps</th>
              <th className="text-left text-sm font-medium text-gray-600 px-6 py-4">Priority</th>
              <th className="text-left text-sm font-medium text-gray-600 px-6 py-4">Status</th>
              <th className="text-right text-sm font-medium text-gray-600 px-6 py-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {useCases.map((useCase) => (
              <tr key={useCase.id} className="border-b border-gray-100 hover:bg-gray-50">
                <td className="px-6 py-4">
                  <div className="font-medium text-gray-900">{useCase.title}</div>
                  {useCase.actor && (
                    <div className="text-sm text-brand-primary mt-0.5">{useCase.actor}</div>
                  )}
                </td>
                <td className="px-6 py-4">
                  <div className="text-gray-700 max-w-md line-clamp-2">
                    {useCase.description || '—'}
                  </div>
                </td>
                <td className="px-6 py-4 text-center">
                  <span className="text-gray-900 font-medium">{Array.isArray(useCase.mainFlow) ? useCase.mainFlow.length : 0}</span>
                </td>
                <td className="px-6 py-4">
                  {useCase.priority && (
                    <span className={cn(
                      "px-3 py-1 text-xs font-medium rounded-full",
                      priorityBadgeColors[useCase.priority]
                    )}>
                      {useCase.priority.charAt(0) + useCase.priority.slice(1).toLowerCase()}
                    </span>
                  )}
                </td>
                <td className="px-6 py-4">
                  <span className={cn(
                    "px-3 py-1 text-xs font-medium rounded-full",
                    statusBadgeColors[useCase.itemStatus]
                  )}>
                    {formatStatus(useCase.itemStatus)}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center justify-end gap-2">
                    <button
                      onClick={() => handleToggleSectionSelector(useCase.id)}
                      className={cn(
                        "p-2 rounded-lg transition-colors",
                        showSectionSelector && askAIItemId === useCase.id
                          ? "bg-indigo-100 text-brand-primary"
                          : "hover:bg-indigo-50 text-brand-primary/60 hover:text-brand-primary"
                      )}
                      title="Ask AI to improve"
                    >
                      <Sparkles className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleOpenDetail(useCase)}
                      className="p-2 hover:bg-gray-100 rounded-lg text-brand-primary hover:text-brand-primary/80"
                      title="View details"
                    >
                      <ArrowRight className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteClick(useCase.id)}
                      className="p-2 hover:bg-red-50 rounded-lg text-red-400 hover:text-red-600"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // Status colors for journeys and storyboards
  const journeyStatusColors: Record<string, string> = {
    COMPLETE: 'bg-green-100 text-green-700',
    IN_PROGRESS: 'bg-blue-100 text-blue-700',
    DRAFT: 'bg-gray-100 text-gray-700',
  };

  const storyboardStatusColors: Record<string, string> = {
    APPROVED: 'bg-green-100 text-green-700',
    REVIEW: 'bg-blue-100 text-blue-700',
    DRAFT: 'bg-gray-100 text-gray-700',
  };

  // Render journeys table (matching Figma design)
  const renderJourneysTable = () => {
    const journeys = items as DefinitionJourney[];
    
    // Count touchpoints from stages
    const countTouchpoints = (journey: DefinitionJourney) => {
      if (!Array.isArray(journey.stages)) return 0;
      return journey.stages.reduce((total, stage) => total + (Array.isArray(stage.steps) ? stage.steps.length : 0), 0);
    };

    // Get persona reference with role - format: "Name - Role"
    const getPersonaWithRole = (journey: DefinitionJourney) => {
      // Check personaRef first (may contain formatted name)
      if (journey.personaRef) return journey.personaRef;
      
      // Check linked personas
      if (Array.isArray(journey.linkedPersonas) && journey.linkedPersonas.length > 0) {
        const persona = journey.linkedPersonas[0].persona;
        // If persona has role, format as "Name - Role"
        const personaData = persona as { name: string; role?: string };
        if (personaData.role) {
          return `${personaData.name} - ${personaData.role}`;
        }
        return personaData.name;
      }
      
      // Fallback to createdBy for display
      if (journey.createdBy) {
        return journey.createdBy.name || journey.createdBy.email?.split('@')[0];
      }
      return null;
    };

    return (
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="text-left text-sm font-normal text-gray-500 px-6 py-4">Journey Name</th>
              <th className="text-left text-sm font-normal text-gray-500 px-6 py-4">Scenario</th>
              <th className="text-center text-sm font-normal text-gray-500 px-6 py-4">Stages</th>
              <th className="text-center text-sm font-normal text-gray-500 px-6 py-4">Touchpoints</th>
              <th className="text-center text-sm font-normal text-gray-500 px-6 py-4">Status</th>
              <th className="text-right text-sm font-normal text-gray-500 px-6 py-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {journeys.map((journey) => {
              const personaWithRole = getPersonaWithRole(journey);
              return (
                <tr key={journey.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-6 py-5">
                    <div>
                      <div className="font-semibold text-gray-900">{journey.scenario}</div>
                      {personaWithRole && (
                        <div className="text-sm text-gray-500 mt-0.5">{personaWithRole}</div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-5">
                    <div className="text-gray-600 line-clamp-2 max-w-[280px]">
                      {journey.description || '—'}
                    </div>
                  </td>
                  <td className="px-6 py-5 text-center">
                    <span className="text-gray-900">{journey.stages?.length || 0}</span>
                  </td>
                  <td className="px-6 py-5 text-center">
                    <span className="text-gray-900">{countTouchpoints(journey)}</span>
                  </td>
                  <td className="px-6 py-5 text-center">
                    <span className={cn(
                      "px-3 py-1 text-xs font-medium rounded-full inline-block",
                      journeyStatusColors[journey.itemStatus] || journeyStatusColors.DRAFT
                    )}>
                      {journey.itemStatus === 'IN_PROGRESS' ? 'In Progress' : 
                       journey.itemStatus === 'COMPLETE' ? 'Complete' : 'Draft'}
                    </span>
                  </td>
                  <td className="px-6 py-5">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => handleToggleSectionSelector(journey.id)}
                        className={cn(
                          "p-2 rounded-lg transition-colors",
                          showSectionSelector && askAIItemId === journey.id
                            ? "bg-indigo-100 text-brand-primary"
                            : "hover:bg-indigo-50 text-brand-primary/60 hover:text-brand-primary"
                        )}
                        title="Ask AI to improve"
                      >
                        <Sparkles className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleOpenDetail(journey)}
                        className="p-2 hover:bg-gray-100 rounded-lg text-brand-primary hover:text-brand-primary/80"
                        title="View details"
                      >
                        <ArrowRight className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteClick(journey.id)}
                        className="p-2 hover:bg-red-50 rounded-lg text-red-400 hover:text-red-500"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  // Render service maps cards
  const renderServiceMapsCards = () => {
    const serviceMaps = items as DefinitionServiceMap[];
    return (
      <div className="space-y-4">
        {serviceMaps.map((serviceMap) => (
          <div key={serviceMap.id} className="bg-white border border-gray-200 rounded-xl p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-lg font-medium text-gray-900">{serviceMap.title}</h3>
                {serviceMap.description && (
                  <p className="text-sm text-gray-500 mt-1">{serviceMap.description}</p>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleToggleSectionSelector(serviceMap.id)}
                  className={cn(
                    "p-2 rounded-lg transition-colors",
                    showSectionSelector && askAIItemId === serviceMap.id
                      ? "bg-indigo-100 text-brand-primary"
                      : "hover:bg-indigo-50 text-brand-primary/60 hover:text-brand-primary"
                  )}
                  title="Ask AI to improve"
                >
                  <Sparkles className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleOpenDetail(serviceMap)}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                  title="View details"
                >
                  <ArrowRight className="w-4 h-4 text-brand-primary" />
                </button>
                <button onClick={() => handleDeleteClick(serviceMap.id)} className="p-2 hover:bg-red-50 rounded-lg">
                  <Trash2 className="w-4 h-4 text-red-400 hover:text-red-600" />
                </button>
              </div>
            </div>
            
            {/* Service Stages */}
            {Array.isArray(serviceMap.serviceStages) && serviceMap.serviceStages.length > 0 && (
              <div className="mb-4">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Service Stages</h4>
                <div className="flex flex-wrap gap-2">
                  {serviceMap.serviceStages.map((stage, i) => (
                    <span key={i} className="px-3 py-1 bg-gray-100 rounded-full text-sm text-gray-700">
                      {stage.name}
                    </span>
                  ))}
                </div>
              </div>
            )}
            
            {serviceMap.failurePoints && (
              <div>
                <h4 className="text-sm font-medium text-red-700 mb-1">Failure Points</h4>
                <p className="text-sm text-gray-600">{serviceMap.failurePoints}</p>
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  // Format date for display
  const formatDate = (date: Date | string | undefined) => {
    if (!date) return '—';
    const d = new Date(date);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Render storyboards table (matching Figma design)
  const renderStoryboardsTable = () => {
    const storyboards = items as DefinitionStoryboard[];

    // Get creator reference string
    const getCreatorRef = (storyboard: DefinitionStoryboard) => {
      if (storyboard.creatorRef) return storyboard.creatorRef;
      if (storyboard.createdBy) {
        return storyboard.createdBy.name || storyboard.createdBy.email?.split('@')[0];
      }
      return null;
    };

    return (
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="text-left text-sm font-normal text-gray-500 px-6 py-4">Title</th>
              <th className="text-left text-sm font-normal text-gray-500 px-6 py-4">Scenario</th>
              <th className="text-center text-sm font-normal text-gray-500 px-6 py-4">Frames</th>
              <th className="text-center text-sm font-normal text-gray-500 px-6 py-4">Created</th>
              <th className="text-center text-sm font-normal text-gray-500 px-6 py-4">Status</th>
              <th className="text-right text-sm font-normal text-gray-500 px-6 py-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {storyboards.map((storyboard) => {
              const creatorRef = getCreatorRef(storyboard);
              return (
                <tr key={storyboard.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-6 py-5">
                    <div>
                      <button
                        onClick={() => handleOpenDetail(storyboard)}
                        className="font-semibold text-gray-900 hover:text-brand-primary transition-colors text-left"
                      >
                        {storyboard.scenario}
                      </button>
                      {creatorRef && (
                        <div className="text-sm text-gray-500 mt-0.5">{creatorRef}</div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-5">
                    <div className="text-gray-600 line-clamp-2 max-w-[280px]">
                      {storyboard.description || storyboard.narrative || '—'}
                    </div>
                  </td>
                  <td className="px-6 py-5 text-center">
                    <span className="text-gray-900">{storyboard.frames?.length || 0}</span>
                  </td>
                  <td className="px-6 py-5 text-center">
                    <span className="text-gray-500">{formatDate(storyboard.createdAt)}</span>
                  </td>
                  <td className="px-6 py-5 text-center">
                    <span className={cn(
                      "px-3 py-1 text-xs font-medium rounded-full inline-block",
                      storyboardStatusColors[storyboard.itemStatus] || storyboardStatusColors.DRAFT
                    )}>
                      {storyboard.itemStatus === 'APPROVED' ? 'Approved' : 
                       storyboard.itemStatus === 'REVIEW' ? 'Review' : 'Draft'}
                    </span>
                  </td>
                  <td className="px-6 py-5">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => handleToggleSectionSelector(storyboard.id)}
                        className={cn(
                          "p-2 rounded-lg transition-colors",
                          showSectionSelector && askAIItemId === storyboard.id
                            ? "bg-indigo-100 text-brand-primary"
                            : "hover:bg-indigo-50 text-brand-primary/60 hover:text-brand-primary"
                        )}
                        title="Ask AI to improve"
                      >
                        <Sparkles className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleOpenDetail(storyboard)}
                        className="p-2 hover:bg-gray-100 rounded-lg text-brand-primary hover:text-brand-primary/80"
                        title="View details"
                      >
                        <ArrowRight className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteClick(storyboard.id)}
                        className="p-2 hover:bg-red-50 rounded-lg text-red-400 hover:text-red-500"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const renderContent = () => {
    if (activeView === 'personas') return renderPersonasTable();
    if (activeView === 'use-cases') return renderUseCasesTable();
    if (activeView === 'journeys') return renderJourneysTable();
    if (activeView === 'service-maps') return renderServiceMapsCards();
    if (activeView === 'storyboards') return renderStoryboardsTable();
    return null;
  };

  // Render form fields based on type
  const renderFormFields = () => {
    switch (activeView) {
      case 'personas':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
              <Input className="bg-white" value={formData.name || ''} onChange={(e) => handleChange('name', e.target.value)} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
              <Input className="bg-white" value={formData.role || ''} onChange={(e) => handleChange('role', e.target.value)} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Demographics</label>
              <Input className="bg-white" value={formData.demographics || ''} onChange={(e) => handleChange('demographics', e.target.value)} placeholder="Age, location, tech proficiency" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Primary Goals</label>
              <Textarea className="bg-white h-[100px]" value={formData.primaryGoals || ''} onChange={(e) => handleChange('primaryGoals', e.target.value)} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Challenges</label>
              <Textarea className="bg-white h-[100px]" value={formData.challenges || ''} onChange={(e) => handleChange('challenges', e.target.value)} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
              <Select value={formData.priority || ''} onValueChange={(v) => handleChange('priority', v)}>
                <SelectTrigger className="bg-white"><SelectValue placeholder="Select priority" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="CRITICAL">Critical</SelectItem>
                  <SelectItem value="HIGH">High</SelectItem>
                  <SelectItem value="MEDIUM">Medium</SelectItem>
                  <SelectItem value="LOW">Low</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        );

      case 'use-cases':
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                <Input className="bg-white" value={formData.title || ''} onChange={(e) => handleChange('title', e.target.value)} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Code</label>
                <Input className="bg-white" value={formData.code || ''} onChange={(e) => handleChange('code', e.target.value)} placeholder="UC-001" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Actor</label>
              <Input className="bg-white" value={formData.actor || ''} onChange={(e) => handleChange('actor', e.target.value)} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <Textarea className="bg-white h-[100px]" value={formData.description || ''} onChange={(e) => handleChange('description', e.target.value)} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Preconditions</label>
              <Textarea className="bg-white h-[80px]" value={formData.preconditions || ''} onChange={(e) => handleChange('preconditions', e.target.value)} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Postconditions</label>
              <Textarea className="bg-white h-[80px]" value={formData.postconditions || ''} onChange={(e) => handleChange('postconditions', e.target.value)} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <Select value={formData.itemStatus || 'DRAFT'} onValueChange={(v) => handleChange('itemStatus', v)}>
                  <SelectTrigger className="bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="DRAFT">Draft</SelectItem>
                    <SelectItem value="IN_REVIEW">In Review</SelectItem>
                    <SelectItem value="APPROVED">Approved</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                <Select value={formData.priority || ''} onValueChange={(v) => handleChange('priority', v)}>
                  <SelectTrigger className="bg-white"><SelectValue placeholder="Select priority" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="CRITICAL">Critical</SelectItem>
                    <SelectItem value="HIGH">High</SelectItem>
                    <SelectItem value="MEDIUM">Medium</SelectItem>
                    <SelectItem value="LOW">Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        );

      case 'journeys':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Scenario *</label>
              <Input className="bg-white" value={formData.scenario || ''} onChange={(e) => handleChange('scenario', e.target.value)} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <Textarea className="bg-white h-[100px]" value={formData.description || ''} onChange={(e) => handleChange('description', e.target.value)} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Pain Points</label>
              <Textarea className="bg-white h-[80px]" value={formData.pains || ''} onChange={(e) => handleChange('pains', e.target.value)} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Opportunities</label>
              <Textarea className="bg-white h-[80px]" value={formData.opportunities || ''} onChange={(e) => handleChange('opportunities', e.target.value)} />
            </div>
          </div>
        );

      case 'service-maps':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
              <Input className="bg-white" value={formData.title || ''} onChange={(e) => handleChange('title', e.target.value)} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <Textarea className="bg-white h-[100px]" value={formData.description || ''} onChange={(e) => handleChange('description', e.target.value)} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Systems</label>
              <Input className="bg-white" value={formData.systems || ''} onChange={(e) => handleChange('systems', e.target.value)} placeholder="Comma-separated list" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Failure Points</label>
              <Textarea className="bg-white h-[80px]" value={formData.failurePoints || ''} onChange={(e) => handleChange('failurePoints', e.target.value)} />
            </div>
          </div>
        );

      case 'storyboards':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Scenario *</label>
              <Input className="bg-white" value={formData.scenario || ''} onChange={(e) => handleChange('scenario', e.target.value)} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <Textarea className="bg-white h-[100px]" value={formData.description || ''} onChange={(e) => handleChange('description', e.target.value)} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Narrative</label>
              <Textarea className="bg-white h-[80px]" value={formData.narrative || ''} onChange={(e) => handleChange('narrative', e.target.value)} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Assumptions</label>
              <Textarea className="bg-white h-[80px]" value={formData.assumptions || ''} onChange={(e) => handleChange('assumptions', e.target.value)} />
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  // Initialize currentVersionId to the latest version when versions load
  useEffect(() => {
    if (versions?.length && !currentVersionId) {
      setCurrentVersionId(versions[0].id);
    }
  }, [versions, currentVersionId]);

  // Convert versions to Version[] type
  const versionOptions: Version[] = versions?.map(v => ({
    id: v.id,
    name: `Version ${v.version}`,
    isCurrent: currentVersionId ? v.id === currentVersionId : v.id === versions?.[0]?.id,
    timestamp: new Date(v.createdAt).toLocaleString(),
  })) || [];

  // Handle edit from detail view
  const handleEditFromDetail = () => {
    if (selectedItem) {
      handleOpenDialog(selectedItem);
    }
  };

  // If an item is selected, render detail view
  // Handle save from detail view
  const handleSaveFromDetail = async (data: Record<string, any>) => {
    if (!selectedItem) return;
    
    // Convert camelCase field names to snake_case for the API
    const snakeCaseData: Record<string, any> = {};
    const fieldMapping: Record<string, string> = {
      name: 'name',
      role: 'role',
      demographics: 'demographics',
      primaryGoals: 'primary_goals',
      challenges: 'challenges',
      contexts: 'contexts',
      tools: 'tools',
      motivations: 'motivations',
      accessibilityNeeds: 'accessibility_needs',
      quotes: 'quotes',
    };
    
    for (const [key, value] of Object.entries(data)) {
      const snakeKey = fieldMapping[key] || key;
      if (value !== undefined && value !== null) {
        snakeCaseData[snakeKey] = value;
      }
    }
    
    try {
      await updateMutation.mutateAsync({
        rowId: selectedItem.id,
        data: snakeCaseData,
      });
      // Refresh the selected item with new data
      setSelectedItem({ ...selectedItem, ...data } as typeof selectedItem);
      toast({ 
        title: 'Changes saved', 
        description: 'Your changes have been saved successfully.' 
      });
    } catch (error) {
      console.error('Failed to save:', error);
      toast({ 
        title: 'Save failed', 
        description: 'Failed to save changes. Please try again.',
        variant: 'destructive'
      });
      throw error;
    }
  };

  if (selectedItem) {
    return (
      <div className="flex-1 flex flex-col h-full overflow-hidden">
        <DefinitionDetailView
          item={selectedItem}
          type={activeView}
          projectId={projectId}
          projectName={projectName}
          onBack={handleCloseDetail}
          onEdit={handleEditFromDetail}
          onSave={handleSaveFromDetail}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full w-full">
      {/* FIXED HEADER - Does NOT scroll */}
      <div className="shrink-0 bg-white px-8 py-6 border-b border-gray-200">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-medium text-gray-900 tracking-tight">{config.title}</h1>
            <p className="text-base text-gray-600 mt-1">{config.subtitle} {projectName}</p>
          </div>
          <div className="flex items-center gap-2">
            {/* Ask AI Button */}
            <button
              onClick={() => setShowAskAI(!showAskAI)}
              className="h-[42px] px-4 flex items-center gap-2 rounded-full transition-colors text-white bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700"
            >
              <Sparkles className="w-4 h-4" />
              <span className="text-sm font-medium">Ask AI</span>
            </button>

            {/* Versions Dropdown */}
            <VersionsDropdown versions={versionOptions} onVersionSelect={(v) => handleRestoreVersion(v.id)} />

            {/* Copy Button */}
            <button 
              className="h-[42px] px-4 flex items-center gap-2 border border-gray-300 rounded-full hover:bg-gray-50 transition-colors text-gray-700"
            >
              <Copy className="w-4 h-4" />
              <span className="text-sm font-medium">Copy</span>
            </button>
          </div>
        </div>
      </div>

      {/* SCROLLABLE CONTENT */}
      <div className="flex-1 overflow-y-auto bg-gray-50">
        <div className="container">
        {/* Ask AI Bar */}
        {showAskAI && (
          <div className="px-8 pt-6">
            <AskAIBar
              isOpen={showAskAI}
              onClose={() => {
                setShowAskAI(false);
                setAskAIItemId(null);
                setAskAISection(null);
                setAskAIPatchSuggestion(null);
              }}
              onSend={handleAskAI}
              onApply={handleApplyPatch}
              placeholder={
                askAISection
                  ? `Improve the "${SECTION_OPTIONS[activeView]?.find(s => s.path === askAISection)?.label || askAISection}" section...`
                  : config.askAIPlaceholder
              }
              isLoading={isAskAILoading}
              mode={askAISection ? 'patch' : 'chat'}
            />

            {/* Section Selector */}
            {showSectionSelector && askAIItemId && (
              <div className="mt-3 p-4 bg-white rounded-xl border border-gray-200 shadow-sm">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-gray-700">
                    Select a section to improve with AI
                  </span>
                  <button
                    onClick={() => {
                      setShowSectionSelector(false);
                      setAskAIItemId(null);
                    }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                  {SECTION_OPTIONS[activeView]?.map((section) => {
                    const item = items.find((i: DefinitionItem) => i.id === askAIItemId);
                    const hasValue = item && getValueByPath(item as Record<string, unknown>, section.path);
                    return (
                      <button
                        key={section.path}
                        onClick={() => handleOpenAskAIForSection(askAIItemId, section.path)}
                        className={cn(
                          "text-left p-3 rounded-lg border transition-all",
                          hasValue
                            ? "border-gray-200 hover:border-brand-primary hover:bg-indigo-50"
                            : "border-dashed border-gray-300 hover:border-brand-primary hover:bg-indigo-50 opacity-70"
                        )}
                      >
                        <div className="font-medium text-sm text-gray-900">{section.label}</div>
                        <div className="text-xs text-gray-500 mt-0.5 line-clamp-1">{section.description}</div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

      {/* Loading */}
      {isLoading && (
        <div className="px-8 py-6">
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <table className="w-full">
              <thead className="border-b border-gray-200">
                <tr>
                  {activeView === 'personas' && (
                    <>
                      <th className="px-6 py-4 text-left"><Skeleton className="h-4 w-24" /></th>
                      <th className="px-6 py-4 text-left"><Skeleton className="h-4 w-32" /></th>
                      <th className="px-6 py-4 text-left"><Skeleton className="h-4 w-28" /></th>
                      <th className="px-6 py-4 text-left"><Skeleton className="h-4 w-20" /></th>
                      <th className="px-6 py-4 text-right"><Skeleton className="h-4 w-16" /></th>
                    </>
                  )}
                  {activeView === 'use-cases' && (
                    <>
                      <th className="px-6 py-4 text-left"><Skeleton className="h-4 w-20" /></th>
                      <th className="px-6 py-4 text-left"><Skeleton className="h-4 w-32" /></th>
                      <th className="px-6 py-4 text-left"><Skeleton className="h-4 w-24" /></th>
                      <th className="px-6 py-4 text-left"><Skeleton className="h-4 w-20" /></th>
                      <th className="px-6 py-4 text-left"><Skeleton className="h-4 w-16" /></th>
                      <th className="px-6 py-4 text-right"><Skeleton className="h-4 w-16" /></th>
                    </>
                  )}
                  {activeView === 'journeys' && (
                    <>
                      <th className="px-6 py-4 text-left"><Skeleton className="h-4 w-28" /></th>
                      <th className="px-6 py-4 text-left"><Skeleton className="h-4 w-24" /></th>
                      <th className="px-6 py-4 text-center"><Skeleton className="h-4 w-16" /></th>
                      <th className="px-6 py-4 text-center"><Skeleton className="h-4 w-24" /></th>
                      <th className="px-6 py-4 text-center"><Skeleton className="h-4 w-16" /></th>
                      <th className="px-6 py-4 text-right"><Skeleton className="h-4 w-16" /></th>
                    </>
                  )}
                  {(activeView === 'service-maps' || activeView === 'storyboards') && (
                    <>
                      <th className="px-6 py-4 text-left"><Skeleton className="h-4 w-32" /></th>
                      <th className="px-6 py-4 text-left"><Skeleton className="h-4 w-40" /></th>
                      <th className="px-6 py-4 text-right"><Skeleton className="h-4 w-16" /></th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {[1, 2, 3, 4, 5].map((i) => (
                  <tr key={i}>
                    {activeView === 'personas' && (
                      <>
                        <td className="px-6 py-4"><Skeleton className="h-5 w-32" /></td>
                        <td className="px-6 py-4"><Skeleton className="h-4 w-24" /></td>
                        <td className="px-6 py-4"><Skeleton className="h-4 w-full max-w-[300px]" /></td>
                        <td className="px-6 py-4"><Skeleton className="h-6 w-16 rounded-full" /></td>
                        <td className="px-6 py-4"><div className="flex justify-end gap-2"><Skeleton className="h-4 w-4" /><Skeleton className="h-4 w-4" /></div></td>
                      </>
                    )}
                    {activeView === 'use-cases' && (
                      <>
                        <td className="px-6 py-4"><Skeleton className="h-4 w-16" /></td>
                        <td className="px-6 py-4"><Skeleton className="h-5 w-48" /></td>
                        <td className="px-6 py-4"><Skeleton className="h-4 w-32" /></td>
                        <td className="px-6 py-4"><Skeleton className="h-6 w-20 rounded-full" /></td>
                        <td className="px-6 py-4"><Skeleton className="h-6 w-16 rounded-full" /></td>
                        <td className="px-6 py-4"><div className="flex justify-end gap-2"><Skeleton className="h-4 w-4" /><Skeleton className="h-4 w-4" /></div></td>
                      </>
                    )}
                    {activeView === 'journeys' && (
                      <>
                        <td className="px-6 py-5"><div className="space-y-1"><Skeleton className="h-5 w-40" /><Skeleton className="h-4 w-24" /></div></td>
                        <td className="px-6 py-5"><Skeleton className="h-4 w-full max-w-[280px]" /></td>
                        <td className="px-6 py-5 text-center"><Skeleton className="h-4 w-8 mx-auto" /></td>
                        <td className="px-6 py-5 text-center"><Skeleton className="h-4 w-8 mx-auto" /></td>
                        <td className="px-6 py-5 text-center"><Skeleton className="h-6 w-20 rounded-full mx-auto" /></td>
                        <td className="px-6 py-5"><div className="flex justify-end"><Skeleton className="h-5 w-5" /></div></td>
                      </>
                    )}
                    {(activeView === 'service-maps' || activeView === 'storyboards') && (
                      <>
                        <td className="px-6 py-4"><Skeleton className="h-5 w-48" /></td>
                        <td className="px-6 py-4"><Skeleton className="h-4 w-full max-w-[400px]" /></td>
                        <td className="px-6 py-4"><div className="flex justify-end gap-2"><Skeleton className="h-4 w-4" /><Skeleton className="h-4 w-4" /></div></td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Content */}
      {!isLoading && hasContent && (
        <div className="px-8 py-6">
          {renderContent()}
        </div>
      )}

      {/* Empty State */}
      {!isLoading && !hasContent && (
        <div className="px-8 py-8">
          <div className="max-w-[585px] mx-auto">
            <div className="text-center mb-8">
              <div className="w-20 h-20 mx-auto mb-6 bg-[#e0e7ff] rounded-[14px] flex items-center justify-center">
                <Icon className="w-10 h-10 text-brand-primary" />
              </div>
              <h2 className="text-lg font-medium text-gray-900 mb-3">{config.emptyTitle}</h2>
              <p className="text-base text-gray-600 max-w-[448px] mx-auto leading-6">{config.emptyDescription}</p>
            </div>
            <ArtifactGeneratorInput
              key={activeView}
              placeholder={config.placeholder}
              onGenerate={handleGenerate}
              isGenerating={generateMutation.isPending}
            />
            <p className="text-center text-base text-gray-500 mt-6">
              AI will analyze your input and generate comprehensive content
            </p>
            {config.hasInlineForm && (
              <div className="mt-6 text-center">
                <Button variant="outline" onClick={() => handleOpenDialog()}>
                  Or add manually
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
        </div>
      </div>

      {/* AI Generation Progress Modal */}
      <AIGenerationProgress
        isGenerating={generateMutation.isPending}
        error={generationError}
        onRetry={() => {
          setGenerationError(null);
        }}
        onCancel={() => setGenerationError(null)}
        artifactType={activeView as ArtifactType}
      />

      {/* Edit/Create Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingRow ? `Edit ${config.title.slice(0, -1)}` : `Add New ${config.title.slice(0, -1)}`}
            </DialogTitle>
            <DialogDescription className="sr-only">
              {editingRow ? `Form to edit the selected ${config.title.slice(0, -1).toLowerCase()}` : `Form to create a new ${config.title.slice(0, -1).toLowerCase()}`}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            {renderFormFields()}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleSave} 
              disabled={createMutation.isPending || updateMutation.isPending}
            >
              {(createMutation.isPending || updateMutation.isPending) && (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              )}
              {editingRow ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Version History Modal */}
      <Dialog open={showVersionHistory} onOpenChange={setShowVersionHistory}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Version History</DialogTitle>
            <DialogDescription className="sr-only">
              View and restore previous versions of this item
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 max-h-[400px] overflow-y-auto">
            {versions && versions.length > 0 ? (
              <div className="space-y-3">
                {versions.map((version) => (
                  <div key={version.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <div className="font-medium text-gray-900">Version {version.version}</div>
                      <div className="text-sm text-gray-500 flex items-center gap-2">
                        <Clock className="w-3.5 h-3.5" />
                        {new Date(version.createdAt).toLocaleString()}
                      </div>
                      {version.changelog && (
                        <div className="text-sm text-gray-600 mt-1">{version.changelog}</div>
                      )}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleRestoreVersion(version.id)}
                      disabled={restoreMutation.isPending}
                    >
                      Restore
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-gray-500 py-8">No versions yet</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={deleteConfirmOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Delete Item"
        description="Are you sure you want to delete this item? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        isLoading={isDeleting}
      />
    </div>
  );
}

export type { DefinitionView };
